import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Image as ImageLib } from '@sparknz/spark-pattern-library';
import { LinkRegion } from '@sparknz/tracking';

import extractAEMStyle, { CQStyleMap } from 'shared/utils/extractCQStyle';
import { ImageEditConfig, ImageProps } from './ImageEditConfig';

const StyledImage = styled.div<
  Pick<ImageProps, 'borderRadius' | 'imageWidth'> & { isClickable: boolean }
>`
  ${({ theme: { palette }, borderRadius, imageWidth, isClickable }) => css`
    position: relative;
    display: ${imageWidth === 'fullWidth' ? 'block' : 'inline-block'};
    img {
      display: block;
      ${imageWidth === 'fullWidth' ? 'width: 100%; height:100%;' : ''}
      ${borderRadius
        ? `border-radius:${borderRadius};`
        : `border-radius: none;`}
    }

    ${isClickable &&
    css`
      &:hover:after {
        opacity: 0.2;
      }

      &:after {
        transition: opacity ease-in-out 0.1s;
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${palette.baseColors.newZealandBlack};
        opacity: 0;
        ${borderRadius
          ? `border-radius:${borderRadius};`
          : `border-radius: none;`}
      }

      &:active:after {
        opacity: 0.4;
      }
    `}
  `}
`;

const StyledAnchor = styled('a')`
  text-decoration: none;
  color: inherit;
  cursor: pointer;
`;

export type ImageVariant = 'fullWidth';

const variantPropAEMStyleMap: CQStyleMap<ImageVariant> = {
  'image--fullwidth': 'fullWidth',
};

// Refer: https://github.com/adobe/aem-react-core-wcm-components-base/blob/master/src/authoring/image/v2/ImageV2.tsx
const Image = ({
  borderRadius: authorSelectedBorderRadius,
  ...props
}: ImageProps) => {
  const { borderRadius } = useTheme();
  const tokenizedBorderRadius = authorSelectedBorderRadius as
    | 'r1'
    | 'r2'
    | 'r3'
    | 'r4'
    | 'round';

  const {
    className,
    width,
    height,
    src,
    alt,
    appliedCssClassNames: cssClassNames,
    regionId = 'image',
    imageLink,
  } = props;

  const imageVariant =
    extractAEMStyle(variantPropAEMStyleMap, cssClassNames) || '';

  if (ImageEditConfig.isEmpty(props)) {
    return null;
  }

  const ImageContent = (
    <StyledImage
      borderRadius={borderRadius[tokenizedBorderRadius]}
      imageWidth={imageVariant}
      title={alt}
      isClickable={Boolean(imageLink)}
    >
      <ImageLib
        className={className}
        width={width}
        height={height}
        src={src}
        alt={alt}
      />
    </StyledImage>
  );

  if (imageLink) {
    return (
      <LinkRegion regionId={regionId}>
        <StyledAnchor
          href={imageLink.url}
          target={imageLink.attributes?.target}
          rel={
            imageLink.attributes?.target === '_blank' ? 'noopener' : undefined
          }
        >
          {ImageContent}
        </StyledAnchor>
      </LinkRegion>
    );
  }
  return <LinkRegion regionId={regionId}>{ImageContent}</LinkRegion>;
};

export default Image;
